import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Security as SecurityIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  History as HistoryIcon,
  SwapHoriz as TransferIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import type { Risk, CreateComponentDto, CreateRiskDto } from 'shared';
import { EntityType } from 'shared';
import { componentsService } from '../services/components.service';
import { riskApi } from '../services/risk.service';
import { useSpace } from '../contexts/SpaceContext';
import { useSecurityAssistant } from '../contexts/SecurityAssistantContext';
import { ComponentFormWizard } from '../components/ComponentForm/ComponentFormWizard';
import { RiskForm } from '../components/RiskForm';
import { VersionHistoryDialog } from '../components/History';
import { TransferComponentDialog } from '../components/TransferComponentDialog';
import { useTranslateSuggestionDescription } from '../hooks/useTranslateSuggestionDescription';

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

interface CIAScoreCardProps {
  label: string;
  score: number | undefined;
  description: string;
  color: string;
}

const CIAScoreCard: React.FC<CIAScoreCardProps> = ({
  label,
  score,
  description,
  color,
}) => {
  const numScore =
    typeof score === 'number' ? score : parseFloat(String(score)) || 0;
  const percentage = (numScore / 10) * 100;

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ShieldIcon sx={{ color, fontSize: 20 }} />
          <Typography variant="subtitle2" color="text.secondary">
            {label}
          </Typography>
        </Box>
        <Typography variant="h4" fontWeight="bold" sx={{ color, mb: 1 }}>
          {numScore.toFixed(1)}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: `${color}20`,
            '& .MuiLinearProgress-bar': {
              backgroundColor: color,
              borderRadius: 4,
            },
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

interface RiskLevelChipProps {
  score: number | string | undefined;
}

const RiskLevelChip: React.FC<RiskLevelChipProps> = ({ score }) => {
  const { t } = useTranslation();
  const numScore =
    typeof score === 'number' ? score : parseFloat(String(score)) || 0;

  const getRiskLevel = (
    s: number
  ): { label: string; color: string; bgColor: string } => {
    if (s >= 8)
      return {
        label: t('risk.level.critical'),
        color: '#FE3B00',
        bgColor: '#FE3B0020',
      };
    if (s >= 6)
      return {
        label: t('risk.level.high'),
        color: '#2D3B45',
        bgColor: '#2D3B4520',
      };
    if (s >= 4)
      return {
        label: t('risk.level.medium'),
        color: '#FFBB00',
        bgColor: '#FFBB0020',
      };
    if (s >= 2)
      return {
        label: t('risk.level.low'),
        color: '#42B8B2',
        bgColor: '#42B8B220',
      };
    return {
      label: t('risk.level.minimal'),
      color: '#939597',
      bgColor: '#93959720',
    };
  };

  const { label, color, bgColor } = getRiskLevel(numScore);

  return (
    <Chip
      label={`${numScore.toFixed(1)} - ${label}`}
      size="small"
      sx={{
        backgroundColor: bgColor,
        color: color,
        fontWeight: 600,
      }}
    />
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ComponentDetail: React.FC = () => {
  const { t } = useTranslation();
  const { translateDescription } = useTranslateSuggestionDescription();
  const { componentId } = useParams<{ componentId: string }>();
  const navigate = useNavigate();
  const { currentSpace } = useSpace();
  const queryClient = useQueryClient();
  const { setComponentContext, setRiskContext, clearContext } =
    useSecurityAssistant();

  const [editFormOpen, setEditFormOpen] = useState(false);
  const [riskFormOpen, setRiskFormOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedRiskId, setSelectedRiskId] = useState<string | null>(null);

  // Transfer mutation
  const transferMutation = useMutation({
    mutationFn: (targetSpaceId: string) =>
      componentsService.transferToSpace(componentId ?? '', targetSpaceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['component', componentId] });
      // Invalidate all component list queries (prefix match)
      queryClient.invalidateQueries({ queryKey: ['components'], exact: false });
      setTransferDialogOpen(false);
    },
  });

  // Fetch component details
  const {
    data: component,
    isLoading: componentLoading,
    error: componentError,
  } = useQuery({
    queryKey: ['component', componentId],
    queryFn: () => {
      if (!componentId) throw new Error('No component ID');
      return componentsService.getById(componentId);
    },
    enabled: !!componentId,
  });

  // Fetch risks for this component
  const { data: risks = [], isLoading: risksLoading } = useQuery({
    queryKey: ['risks', 'component', componentId],
    queryFn: async () => {
      if (!currentSpace?.id) return [];
      const allRisks = await riskApi.getAll({ spaceId: currentSpace.id });
      return allRisks.filter((r: Risk) => r.componentId === componentId);
    },
    enabled: !!componentId && !!currentSpace?.id,
  });

  // Set Security Assistant context when component is loaded
  useEffect(() => {
    if (component && currentSpace) {
      setComponentContext({
        spaceId: currentSpace.id,
        componentId: component.id,
        componentName: component.name,
        componentType: component.type,
        confidentialityScore: component.confidentialityScore,
        integrityScore: component.integrityScore,
        availabilityScore: component.availabilityScore,
        dataCategories: component.dataCategories,
        environment: component.environment,
      });
    }
    // Clear context when leaving the page
    return () => {
      clearContext();
    };
  }, [component, currentSpace, setComponentContext, clearContext]);

  // Calculate aggregate risk score
  const aggregateRiskScore = React.useMemo(() => {
    if (risks.length === 0) return 0;
    const totalInherent = risks.reduce((sum: number, r: Risk) => {
      const score =
        typeof r.inherentRisk === 'number'
          ? r.inherentRisk
          : parseFloat(String(r.inherentRisk)) || 0;
      return sum + score;
    }, 0);
    return totalInherent / risks.length;
  }, [risks]);

  const residualRiskScore = React.useMemo(() => {
    if (risks.length === 0) return 0;
    const totalResidual = risks.reduce((sum: number, r: Risk) => {
      const score =
        typeof r.residualRisk === 'number'
          ? r.residualRisk
          : parseFloat(String(r.residualRisk)) || 0;
      return sum + score;
    }, 0);
    return totalResidual / risks.length;
  }, [risks]);

  // Sort risks by residual risk (descending) - highest risk first
  const sortedRisks = React.useMemo(() => {
    return [...risks].sort((a, b) => {
      const aScore =
        typeof a.residualRisk === 'number'
          ? a.residualRisk
          : parseFloat(String(a.residualRisk)) || 0;
      const bScore =
        typeof b.residualRisk === 'number'
          ? b.residualRisk
          : parseFloat(String(b.residualRisk)) || 0;
      return bScore - aScore;
    });
  }, [risks]);

  // Handle component update
  const handleComponentUpdate = async (data: CreateComponentDto) => {
    if (!componentId) return;
    await componentsService.update(componentId, data);
    queryClient.invalidateQueries({ queryKey: ['component', componentId] });
    setEditFormOpen(false);
  };

  // Loading state
  if (componentLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (componentError || !component) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {componentError
            ? (componentError as Error).message
            : t('component.notFound')}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight="bold">
            {component.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(`componentTypes:${component.type}`, component.type)} •{' '}
            {t('component.detail.subtitle')}
          </Typography>
        </Box>
        <Tooltip title={t('history.viewHistory')}>
          <IconButton onClick={() => setHistoryDialogOpen(true)}>
            <HistoryIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('component.transfer.action')}>
          <IconButton onClick={() => setTransferDialogOpen(true)}>
            <TransferIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('common.edit')}>
          <IconButton onClick={() => setEditFormOpen(true)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Description */}
      {component.description && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="body1">
            {translateDescription(component.description)}
          </Typography>
        </Paper>
      )}

      {/* CIA Scores */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        <SecurityIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
        {t('component.detail.ciaScores')}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <CIAScoreCard
            label={t('classification.confidentiality.title')}
            score={component.confidentialityScore}
            description={t('classification.confidentiality.description')}
            color="#2D3B45"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <CIAScoreCard
            label={t('classification.integrity.title')}
            score={component.integrityScore}
            description={t('classification.integrity.description')}
            color="#42B8B2"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <CIAScoreCard
            label={t('classification.availability.title')}
            score={component.availabilityScore}
            description={t('classification.availability.description')}
            color="#FFBB00"
          />
        </Grid>
      </Grid>

      {/* Risk Overview */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography variant="h6">
          <WarningIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          {t('component.detail.risks')} ({risks.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setRiskFormOpen(true)}
          size="small"
        >
          {t('risk.create')}
        </Button>
      </Box>

      {/* Aggregate Risk Summary */}
      {risks.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('component.detail.avgInherentRisk')}
              </Typography>
              <RiskLevelChip score={aggregateRiskScore} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('component.detail.avgResidualRisk')}
              </Typography>
              <RiskLevelChip score={residualRiskScore} />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Risk Table */}
      {risksLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : risks.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <WarningIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {t('component.detail.noRisks')}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setRiskFormOpen(true)}
            sx={{ mt: 2 }}
          >
            {t('risk.create')}
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('risk.fields.title')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('risk.fields.threatSource')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  {t('risk.fields.inherentRisk')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  {t('risk.fields.residualRisk')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('risk.fields.status')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('risk.fields.treatment')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRisks.map((risk: Risk) => (
                <TableRow
                  key={risk.id}
                  hover
                  selected={selectedRiskId === risk.id}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    // Single click: select risk and set Security Assistant context
                    setSelectedRiskId(risk.id);
                    if (currentSpace) {
                      setRiskContext({
                        spaceId: currentSpace.id,
                        riskId: risk.id,
                        riskTitle: risk.title,
                        componentId: component?.id,
                        componentName: component?.name,
                        inherentRisk: risk.inherentRisk,
                        residualRisk: risk.residualRisk,
                        threatSource: risk.threatSource,
                        vulnerability: risk.vulnerability,
                      });
                    }
                  }}
                  onDoubleClick={() => {
                    // Double click: navigate to risk detail page
                    navigate(`/risks/${risk.id}`);
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {risk.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {risk.threatSource || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <RiskLevelChip score={risk.inherentRisk} />
                  </TableCell>
                  <TableCell align="center">
                    <RiskLevelChip score={risk.residualRisk} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`risk.status.${risk.status}`)}
                      size="small"
                      variant="outlined"
                      color={
                        risk.status === 'closed' || risk.status === 'mitigated'
                          ? 'success'
                          : risk.status === 'identified'
                            ? 'warning'
                            : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(`risk.treatment.${risk.riskTreatment}`)}
                      size="small"
                      sx={{
                        backgroundColor:
                          risk.riskTreatment === 'mitigate'
                            ? '#00D9FF20'
                            : risk.riskTreatment === 'accept'
                              ? '#FFB30020'
                              : '#2D3B4520',
                        color:
                          risk.riskTreatment === 'mitigate'
                            ? '#00D9FF'
                            : risk.riskTreatment === 'accept'
                              ? '#FFB300'
                              : '#2D3B45',
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Component Metadata */}
      <Divider sx={{ my: 4 }} />
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('component.detail.metadata')}
      </Typography>
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {t('component.type')}
            </Typography>
            <Typography variant="body2">
              {t(`componentTypes:${component.type}`, component.type)}
            </Typography>
          </Grid>
          {component.dataCategories && component.dataCategories.length > 0 && (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="caption" color="text.secondary">
                {t('component.dataCategories')}
              </Typography>
              <Box
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}
              >
                {component.dataCategories.map((cat: string) => (
                  <Chip
                    key={cat}
                    label={t(`categories:${cat}.name`, cat)}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Grid>
          )}
          {component.regulatoryRequirements &&
            component.regulatoryRequirements.length > 0 && (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('component.regulatoryRequirements')}
                </Typography>
                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}
                >
                  {component.regulatoryRequirements.map((req: string) => (
                    <Chip
                      key={req}
                      label={t(`regulatory:${req}.name`, req)}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>
            )}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {t('common.createdAt')}
            </Typography>
            <Typography variant="body2">
              {new Date(component.createdAt).toLocaleDateString()}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Edit Component Form */}
      {currentSpace && (
        <ComponentFormWizard
          open={editFormOpen}
          onClose={() => setEditFormOpen(false)}
          onSubmit={handleComponentUpdate}
          component={component}
          currentSpaceId={currentSpace.id}
        />
      )}

      {/* Create Risk Form */}
      {componentId && currentSpace && (
        <RiskForm
          open={riskFormOpen}
          onClose={() => {
            setRiskFormOpen(false);
            queryClient.invalidateQueries({
              queryKey: ['risks', 'component', componentId],
            });
          }}
          onSubmit={async data => {
            await riskApi.create(data as CreateRiskDto);
            queryClient.invalidateQueries({
              queryKey: ['risks', 'component', componentId],
            });
            setRiskFormOpen(false);
          }}
          componentId={componentId}
          spaceId={currentSpace.id}
          mode="create"
        />
      )}

      {/* Version History Dialog */}
      {component && (
        <VersionHistoryDialog
          open={historyDialogOpen}
          onClose={() => setHistoryDialogOpen(false)}
          entityType={EntityType.COMPONENT}
          entityId={component.id}
          entityName={component.name}
        />
      )}

      {/* Transfer Component Dialog */}
      {component && (
        <TransferComponentDialog
          open={transferDialogOpen}
          onClose={() => setTransferDialogOpen(false)}
          onTransfer={async targetSpaceId => {
            await transferMutation.mutateAsync(targetSpaceId);
          }}
          componentName={component.name}
          currentSpaceId={component.spaceId}
          isLoading={transferMutation.isPending}
        />
      )}
    </Box>
  );
};
